import {
  addDays,
  formatDistanceToNowStrict,
  fromUnixTime,
  getUnixTime,
  isBefore,
  subDays,
} from "date-fns";
import { GraphQLClient } from "graphql-request";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useInfiniteQuery } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useRouteMatch } from "react-router-dom";
import Avatar from "../../../components/Avatar";
import { BookingDetails } from "../../../components/Brand/BookingDetails";
import { CTA } from "../../../components/CTA";
import { CardLink } from "../../../components/CardLink";
import { Flex } from "../../../components/Flex";
import { H1, H2, H3 } from "../../../components/Heading";
import { HealthGuage } from "../../../components/HealthGuage";
import Loading from "../../../components/Loading";
import { Modal } from "../../../components/Modal";
import { Subtitle } from "../../../components/Subtitle";
import { Tab } from "../../../components/Tab";
import { Text } from "../../../components/Text";
import { ListingEmptyStateIcon } from "../../../components/icons/ListingEmptyStateIcon";
import { StarIcon } from "../../../components/icons/StarIcon";
import {
  LARGE_DESKTOP_BREAKPOINT,
  MEDIUM_DESKTOP_BREAKPOINT,
  MOBILE_BREAKPOINT,
  XL_DESKTOP_BREAKPOINT,
} from "../../../config";
import {
  AccountType,
  BookingsSortBy,
  BookingStatus,
  BrandBookingsDocument,
  BrandBookingsQuery,
  CreatorTier,
  ListingStatus,
  useBrandListingsQuery,
  useBrandNotificationCountQuery,
  useGetBookingQuery,
  useGetBrandApprovalResponseTimesQuery,
} from "../../../graphql/generated";
import { queryKeys } from "../../../graphql/queryKeys";
import { useDemoBookingData } from "../../../hooks/useDemoBookingData";
import { useDemoMode } from "../../../hooks/useDemoMode";
import useGqlClient from "../../../hooks/useGqlClient";
import { authSelectors } from "../../../store/auth/selector";
import { actions } from "../../../store/auth/slice";
import { css, styled } from "../../../styles";
import { BookingTabs } from "../../../utils/enums";
import { ApprovalTimeModal } from "./ApprovalTimeModal";
import { ArchivedBookings } from "./Archived";
import { CompletedBookings } from "./Completed";
import { UpcomingBookings } from "./Upcoming";

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 1600px;
  max-height: 92vh;
  height: 100%;
  padding: 0 ${(p) => p.theme.spacing.l};
  box-sizing: border-box;

  @media (min-width: ${LARGE_DESKTOP_BREAKPOINT}px) {
    padding: 0 ${(p) => p.theme.spacing.xl};
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 0 ${(p) => p.theme.spacing.l} ${(p) => p.theme.spacing.xl};
    max-height: unset;
  }
`;

const Grid = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  min-height: 100%;
`;

const BookingsWrap = styled.div`
  background: ${(p) => p.theme.color.card.background};
  box-shadow: ${(p) => p.theme.shadow.card};
  border-radius: ${(p) => p.theme.misc.borderRadius};
  box-sizing: border-box;
  overflow: hidden;
  height: 100%;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    background: transparent;
    box-shadow: none;
    overflow: unset;
  }
`;

const FirstColumn = styled.div`
  overflow-y: scroll;
  width: 100%;
  overflow-x: hidden;
  max-width: 320px;
  border-right: 1px solid ${(p) => p.theme.color.card.divider};
  background-color: ${(p) => p.theme.color.card.callout};

  @media (min-width: ${MEDIUM_DESKTOP_BREAKPOINT}px) {
    max-width: 360px;
  }

  @media (min-width: ${XL_DESKTOP_BREAKPOINT}px) {
    max-width: 400px;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 100%;
    min-width: 0px;
    max-width: 100%;
    background: transparent;
    overflow: visible;
    border-width: 0px;
  }

  ${(p) => p.theme.color.primary}
`;

const SecondColumn = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: scroll;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: none;
  }
`;

const StyledCardLink = styled(CardLink)<{ isActive: boolean }>`
  border-left: 3px solid
    ${(p) => (p.isActive ? p.theme.color.primary : "transparent")};
  padding: ${(p) => p.theme.spacing.xs} 0;
  background-color: ${(p) => p.theme.color.card.background};
  transition: border-color 0.1s ease-out;
  transition: background-color 0.1s ease-out;
  position: relative;

  ${(p) =>
    p.isActive
      ? css`
          background-color: ${(p) => p.theme.color.primary}09;
        `
      : css``}

  &:hover {
    background-color: ${(p) => p.theme.color.primary}09;
  }

  border-bottom: 1px solid ${(p) => p.theme.color.card.divider};

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    border: none;
    background: ${(p) => p.theme.color.card.background};
    box-shadow: ${(p) => p.theme.shadow.card};
    border-radius: ${(p) => p.theme.misc.borderRadius};
    margin-bottom: ${(p) => p.theme.spacing.m};
  }
`;

const HorizontalScroll = styled.div`
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 100%;
    white-space: nowrap;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none; /* For Firefox */
    -ms-overflow-style: none; /* For Internet Explorer and Edge */
  }
`;

const TabWrap = styled(Flex)`
  & > *:not(:first-child) {
    margin-left: ${(p) => p.theme.spacing.l};
  }

  & > *:last-child {
    margin-left: auto;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: flex;
    white-space: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }
`;

export interface MatchParams {
  page: BookingTabs;
  id: string | undefined;
  bookingPostId?: string | undefined;
}

function getTabFromBooking(status: BookingStatus, inProgress: boolean) {
  switch (status) {
    case BookingStatus.BookingStatusPending:
      return BookingTabs.New;
    case BookingStatus.BookingStatusCreatorRescheduled:
      return BookingTabs.New;
    case BookingStatus.BookingStatusApproved:
      if (inProgress) {
        return BookingTabs.InProgress;
      }
      return BookingTabs.Upcoming;
    case BookingStatus.BookingStatusCompleted:
      return BookingTabs.Completed;
    // archived
    default:
      return BookingTabs.Archived;
  }
}

function useBookingStatusToDeterminePage(
  client: GraphQLClient,
  bookId?: string
) {
  const { data, isLoading, isError } = useGetBookingQuery(
    client,
    { id: bookId ? bookId : "" },
    {
      enabled: !!bookId,
      retry: false,
    }
  );

  if (!data || isLoading || isError) {
    return { tab: null, isLoading, isError };
  }

  const tab = getTabFromBooking(data.booking.status, data.booking.inProgress);

  return { tab, isLoading, isError };
}

export const BrandBookingsView = () => {
  const [page, setPage] = useState<BookingTabs>(BookingTabs.New);
  const activeBrandId = useSelector(authSelectors.activeBrandId);
  let idMatch = useRouteMatch<MatchParams>("/b/bookings/:page/:id");
  let pageMatch = useRouteMatch<MatchParams>("/b/bookings/:page");
  let bookingPostIdMatch = useRouteMatch<MatchParams>(
    "/b/bookings/:page/:id/review/:bookingPostId"
  );
  const [activeBookingId, setActiveBookingId] = useState<string | null>(null);
  const [activeBookingPostSubmissionId, setActiveBookingPostSubmissionId] =
    useState<string | null>(null);
  const history = useHistory();
  const client = useGqlClient();
  const dispatch = useDispatch();
  const profile = useSelector(authSelectors.activeBrand);
  const [approvalTimeModalOpen, setApprovalTimeModalOpen] = useState(false);
  const account = useSelector(authSelectors.account);

  const { isDemoMode } = useDemoMode();

  let { tab } = useBookingStatusToDeterminePage(client, idMatch?.params.id);

  const { data: notificationCountData } = useBrandNotificationCountQuery(
    client,
    {
      brandID: activeBrandId ? activeBrandId : "",
    },
    {
      enabled: activeBrandId !== "",
      refetchOnWindowFocus: false,
    }
  );

  const today = useMemo(() => new Date(), []);

  const { data: approvalTimeData } = useGetBrandApprovalResponseTimesQuery(
    client,
    {
      id: activeBrandId ? activeBrandId : "",
      startDate: getUnixTime(addDays(today, -30)),
      filters: {
        startDate: getUnixTime(addDays(today, -30)),
      },
    },
    {
      enabled:
        account && account.type === AccountType.AccountTypeStaff ? true : false,
    }
  );

  // Handle brand ID changes from query params
  useEffect(() => {
    const q = new URLSearchParams(history.location.search);
    const newActiveBrandId = q.get("activeBrandId");

    if (newActiveBrandId && newActiveBrandId !== activeBrandId) {
      dispatch(actions.setActiveBrand({ brandId: newActiveBrandId }));
      dispatch(actions.setActiveGroup({ groupId: "" }));
    }
  }, [history.location.search, dispatch, activeBrandId]);

  // Reset selections when brand OR page changes
  useEffect(() => {
    setActiveBookingId(null);
    setActiveBookingPostSubmissionId(null);
  }, [activeBrandId, page]);

  // Handle page changes from URL
  useEffect(() => {
    if (pageMatch && pageMatch.params.page !== page) {
      setPage(pageMatch.params.page);
    }
  }, [pageMatch, page]);

  // Sync booking post submission ID with URL
  useEffect(() => {
    const newId = bookingPostIdMatch?.params.bookingPostId || null;
    setActiveBookingPostSubmissionId(newId);
  }, [bookingPostIdMatch]);

  // Complex booking ID and URL synchronization
  useEffect(() => {
    if (!idMatch) return;

    const newActiveBookingId = idMatch.params.id || null;

    // Determine the correct tab based on booking status or route
    const routePage = pageMatch?.params.page as BookingTabs | undefined;

    // If we're waiting for booking status, just set the ID without changing URL
    if (!isDemoMode && tab == null) {
      if (activeBookingId !== newActiveBookingId) {
        setActiveBookingId(newActiveBookingId);
      }
      return;
    }

    // Calculate the target tab
    const newTab = isDemoMode
      ? routePage || page // Demo mode: use route or current page
      : tab || routePage || page; // Normal: use determined tab, then fallbacks

    // Check if we need to update
    const needsBookingUpdate = activeBookingId !== newActiveBookingId;
    const needsTabUpdate = page !== newTab;

    if (!needsBookingUpdate && !needsTabUpdate) {
      return; // No changes needed
    }

    // Update state
    if (needsBookingUpdate) {
      setActiveBookingId(newActiveBookingId);
    }

    // Build and update URL if needed
    let newPath = `/b/bookings/${newTab}/${newActiveBookingId}`;

    // Preserve review segment if exists
    const reviewMatch = window.location.pathname.match(/\/review\/[^/]+$/);
    if (reviewMatch) {
      newPath += reviewMatch[0];
    }

    // Only update URL if it actually changes
    if (window.location.pathname !== newPath) {
      const currentSearch = history.location.search;
      history.replace(`${newPath}${currentSearch}`);
    }
  }, [
    idMatch,
    tab,
    pageMatch,
    activeBookingId,
    page,
    history,
    isDemoMode,
    setActiveBookingId,
  ]);

  if (!profile) {
    return <Loading defer />;
  }

  const newBadgeCount = isDemoMode
    ? 4
    : notificationCountData?.brand?.numApplicationsToReview ?? 0;

  const actionRequiredBadgeCount =
    notificationCountData?.brand?.numBookingsToAcknowledge ?? 0;

  const numBookingsToReview =
    notificationCountData?.brand?.numBookingsToReview ?? 0;

  const numBookingPostSubmissionsToReview =
    notificationCountData?.brand?.numBookingPostSubmissionsToReview ?? 0;

  const numUnreadMessageCountBookingsUpcoming =
    notificationCountData?.brand?.unreadMessageCountBookingsUpcoming ?? 0;

  const numUnreadMessageCountBookingsInProgress =
    notificationCountData?.brand?.unreadMessageCountBookingsInProgress ?? 0;

  const tabOnClick = (page: BookingTabs) => {
    // Preserve existing query parameters when switching tabs
    history.push(`/b/bookings/${page}`);
  };

  const avgDays = approvalTimeData?.brand?.approvalResponseTimeAverage ?? null;
  const benchmarkDays =
    approvalTimeData?.brand?.approvalResponseTimeBenchmarkTopSegment ?? null;

  // Higher is better: 100% means at or faster than benchmark; clamp 0–100
  // If there are no approved bookings (avg == 0), show 0%
  const approvalPct =
    avgDays === 0
      ? 0
      : avgDays && benchmarkDays
      ? Math.max(0, Math.min(100, Math.round((benchmarkDays / avgDays) * 100)))
      : 0;

  return (
    <>
      <Modal
        isOpen={approvalTimeModalOpen}
        onClose={() => setApprovalTimeModalOpen(false)}
      >
        <ApprovalTimeModal
          onClose={() => setApprovalTimeModalOpen(false)}
          onConfirm={() => setApprovalTimeModalOpen(false)}
        />
      </Modal>
      <Wrap>
        <Flex justify="space-between" align="center" margin="0 0 s">
          <H1 margin="0 0 0">Bookings</H1>
          {approvalTimeData &&
          account &&
          account.type === AccountType.AccountTypeStaff ? (
            <HealthGuage
              percentage={approvalPct}
              onClick={() => setApprovalTimeModalOpen(true)}
            />
          ) : null}
        </Flex>
        <HorizontalScroll>
          <TabWrap margin="0 0 l" justify="flex-start">
            <Tab
              textAlign="left"
              active={page === BookingTabs.New}
              label="New Applications"
              onClick={() => tabOnClick(BookingTabs.New)}
              badgeCount={newBadgeCount}
              hideInactiveBorder
              textSize="m"
            />
            <Tab
              textAlign="left"
              active={page === BookingTabs.Upcoming}
              label="Upcoming"
              onClick={() => tabOnClick(BookingTabs.Upcoming)}
              badgeCount={
                actionRequiredBadgeCount + numUnreadMessageCountBookingsUpcoming
              }
              hideInactiveBorder
              textSize="m"
            />
            <Tab
              textAlign="left"
              active={page === BookingTabs.InProgress}
              label="In Progress"
              onClick={() => tabOnClick(BookingTabs.InProgress)}
              hideInactiveBorder
              badgeCount={
                numBookingPostSubmissionsToReview +
                numUnreadMessageCountBookingsInProgress
              }
              textSize="m"
            />
            <Tab
              textAlign="left"
              active={page === BookingTabs.Completed}
              label="Completed"
              onClick={() => tabOnClick(BookingTabs.Completed)}
              hideInactiveBorder
              textSize="m"
              badgeCount={numBookingsToReview}
            />
            <Tab
              textAlign="left"
              active={page === BookingTabs.Archived}
              label="Archived"
              onClick={() => tabOnClick(BookingTabs.Archived)}
              hideInactiveBorder
              textSize="m"
            />
          </TabWrap>
        </HorizontalScroll>
        <RenderBookings
          page={page}
          client={client}
          activeBookingId={activeBookingId}
          activeBookingPostSubmissionId={activeBookingPostSubmissionId}
          setActiveBookingId={setActiveBookingId}
          demoMode={isDemoMode ?? false}
        />
      </Wrap>
    </>
  );
};

const RenderBookings = ({
  page,
  client,
  activeBookingId,
  activeBookingPostSubmissionId,
  setActiveBookingId,
  demoMode,
}: {
  client: GraphQLClient;
  page: BookingTabs;
  activeBookingId: string | null;
  activeBookingPostSubmissionId: string | null;
  setActiveBookingId: (id: string | null) => void;
  demoMode: boolean;
}) => {
  const observer = useRef<IntersectionObserver | null>(null);
  const brandId = useSelector(authSelectors.activeBrandId);
  const [statuses, setStatuses] = useState<BookingStatus[]>([
    BookingStatus.BookingStatusPending,
  ]);
  const [sortBy, setSortBy] = useState<BookingsSortBy | undefined>(
    BookingsSortBy.BookingsSortByPremiumThenCreatedAtAsc
  );
  const [isInProgress, setIsInProgress] = useState<boolean | null>(null);
  const [count, setCount] = useState(10);
  const { getDemoBookingIds } = useDemoMode();
  const bookingIDs = demoMode ? getDemoBookingIds(page) : undefined;

  const serializedFilters = useMemo(
    () =>
      JSON.stringify({
        statuses,
        isAwaitingContent: isInProgress,
        count,
        sortBy,
        bookingIDs,
      }),
    [statuses, isInProgress, count, sortBy, bookingIDs]
  );

  const fetchBookings = useCallback(
    async ({ pageParam = "" }) => {
      return client.request<BrandBookingsQuery>(BrandBookingsDocument, {
        cursor: pageParam,
        statuses,
        isAwaitingContent: isInProgress,
        count,
        sortBy,
        bookingIDs,
      });
    },
    [client, statuses, isInProgress, count, sortBy, bookingIDs]
  );

  const {
    data,
    isLoading,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(
    [queryKeys.brandBookings, brandId, page, serializedFilters],
    fetchBookings,
    {
      getNextPageParam: (lastPage) => lastPage.bookings.cursor || undefined,
    }
  );

  const bookings = useMemo(() => {
    return data?.pages.flatMap((page) => page.bookings.bookings) || [];
  }, [data?.pages]);

  // Track if we've set initial booking to prevent infinite loops
  const history = useHistory();
  const hasSetInitialBooking = useRef(false);

  // Clear active booking and reset flag when page changes
  useEffect(() => {
    setActiveBookingId(null);
    hasSetInitialBooking.current = false;
  }, [page, setActiveBookingId]);

  // Auto-select first booking for New and InProgress tabs (when no valid booking in URL)
  useEffect(() => {
    // Only auto-select for specific tabs
    const shouldAutoSelect =
      page === BookingTabs.New || page === BookingTabs.InProgress;
    if (!shouldAutoSelect) return;

    // Don't auto-select if:
    // 1. We already have a booking ID selected
    // 2. We're waiting for data
    // 3. No bookings available
    // 4. We've already auto-selected for this page
    if (
      activeBookingId || // Already have a booking selected
      !data ||
      bookings.length === 0 ||
      hasSetInitialBooking.current
    ) {
      return;
    }

    // Check if the URL already has a booking ID that exists in our bookings
    const urlBookingId = window.location.pathname.match(
      /\/b\/bookings\/[^/]+\/([^/]+)/
    )?.[1];
    if (urlBookingId && bookings.some((b) => b.id === urlBookingId)) {
      // URL already has a valid booking ID for this page, don't auto-select
      return;
    }

    // Only auto-select on desktop
    // eg. allow b/bookings/in_progress instead of forcing b/bookings/in_progress/book_123
    const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
    if (isMobile) return;

    // Select the appropriate booking
    const bookingIdToSelect =
      demoMode && page === BookingTabs.New
        ? bookings[bookings.length - 1].id // Demo: newest first for New tab
        : bookings[0].id; // Normal: first booking

    setActiveBookingId(bookingIdToSelect);
    hasSetInitialBooking.current = true;

    // Update URL with the selected booking
    const currentSearch = history.location.search;
    history.replace(`/b/bookings/${page}/${bookingIdToSelect}${currentSearch}`);
  }, [
    data,
    bookings,
    activeBookingId,
    demoMode,
    setActiveBookingId,
    page,
    history,
  ]);

  // Set filters based on current page
  useEffect(() => {
    let newSortBy: BookingsSortBy | undefined;
    let newIsInProgress: boolean | null;
    let newStatuses: BookingStatus[];
    const newCount = 10;

    switch (page) {
      case BookingTabs.New:
        newSortBy = BookingsSortBy.BookingsSortByPremiumThenCreatedAtAsc;
        newIsInProgress = null;
        newStatuses = demoMode
          ? [BookingStatus.BookingStatusCompleted]
          : [
              BookingStatus.BookingStatusPending,
              BookingStatus.BookingStatusCreatorRescheduled,
            ];
        break;

      case BookingTabs.Upcoming:
        // No filter changes needed for Upcoming
        return;

      case BookingTabs.InProgress:
        newSortBy = BookingsSortBy.BookingsSortByUpdatedAt;
        newIsInProgress = true;
        newStatuses = demoMode
          ? [BookingStatus.BookingStatusCompleted]
          : [BookingStatus.BookingStatusApproved];
        break;

      case BookingTabs.Completed:
        newSortBy = BookingsSortBy.BookingsSortByCompletedAt;
        newIsInProgress = null;
        newStatuses = [BookingStatus.BookingStatusCompleted];
        break;

      default: // Archived
        newSortBy = BookingsSortBy.BookingsSortByLastActivityDate;
        newIsInProgress = null;
        newStatuses = [
          BookingStatus.BookingStatusCancelled,
          BookingStatus.BookingStatusExpired,
          BookingStatus.BookingStatusRejected,
        ];
    }

    // Batch state updates to minimize re-renders
    setSortBy(newSortBy);
    setIsInProgress(newIsInProgress);
    setStatuses(newStatuses);
    setCount(newCount);
  }, [page, demoMode]);

  const lastElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoading, hasNextPage, fetchNextPage]
  );

  if (isLoading) {
    return <Loading defer />;
  }

  if (isError) {
    return (
      <div>
        <H3>Oops</H3>
        <Text margin={"0 0 xl 0"}>
          Couldn't load your bookings, please try again later.
        </Text>
      </div>
    );
  }

  if (bookings.length === 0 && page !== BookingTabs.Upcoming) {
    return (
      <Flex margin="xl 0 0" justify="center" align="center" direction="column">
        <EmptyState page={page} />
      </Flex>
    );
  }

  if (page === BookingTabs.Completed) {
    return (
      <CompletedBookings
        bookings={bookings}
        lastElementRef={lastElementRef}
        isFetching={isFetchingNextPage}
      />
    );
  }

  if (page === BookingTabs.Upcoming) {
    return <UpcomingBookings />;
  }

  if (page === BookingTabs.Archived) {
    return (
      <ArchivedBookings
        bookings={bookings}
        lastElementRef={lastElementRef}
        isFetching={isFetchingNextPage}
      />
    );
  }

  return (
    <BookingsWrap>
      <Grid>
        <FirstColumn>
          <RenderBookingList
            bookings={bookings}
            bookingType={page}
            lastElementRef={lastElementRef}
            activeBookingId={activeBookingId}
            isDemo={demoMode}
          />
        </FirstColumn>
        <SecondColumn>
          <BookingDetails
            bookingId={activeBookingId}
            activeBookingPostSubmissionId={activeBookingPostSubmissionId}
          />
        </SecondColumn>
      </Grid>
    </BookingsWrap>
  );
};

interface EmptyStateProps {
  page: BookingTabs;
}

const EmptyStateWrap = styled.div`
  margin-top: 80px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    margin-top: 32px;
  }
`;

const EmptyState = ({ page }: EmptyStateProps) => {
  const activeBrand = useSelector(authSelectors.activeBrand);
  const client = useGqlClient();
  const { data, isLoading } = useBrandListingsQuery(
    client,
    { brandId: activeBrand?.id },
    {
      retry: false,
      // refetchOnWindowFocus: false,
    }
  );

  const noListings =
    data &&
    data.listings.listings.filter(
      (l) => l.status === ListingStatus.StatusActive
    ).length === 0;

  if (isLoading) {
    return <Loading defer />;
  }

  if (noListings) {
    return (
      <EmptyStateWrap>
        <Flex
          margin="xxxl 0 0"
          justify="center"
          direction="column"
          align="center"
          style={{ textAlign: "center" }}
        >
          <ListingEmptyStateIcon />
          <H2 margin="xxl 0 m">No Active Listings</H2>
          <Subtitle margin="0">
            Create a live listing to start receiving applications
          </Subtitle>
          <div>
            <CTA margin="l 0 0" to={"/b/listings"}>
              Create Campaign
            </CTA>
          </div>
        </Flex>
      </EmptyStateWrap>
    );
  }

  let header = "No Applications";
  let subtitle =
    "We'll email you as soon as there's a new application to review";
  let buttonText = "Create Campaign";
  let buttonUrl = "/b/listings";
  switch (page) {
    case BookingTabs.New:
      header = "No Applications";
      subtitle =
        "We'll email you as soon as there's a new application to review";
      buttonText = "";
      buttonUrl = "";
      break;
    // case BookingTabs.Upcoming:
    //   header = "You don't currently have any new applications";
    //   subtitle =
    //     "We'll notify you as soon an influencer has applied for your listing.";
    //   buttonText = "Create listing";
    //   buttonUrl = "/b/listings";
    //   break;
    case BookingTabs.InProgress:
      header = "No Bookings In Progress";
      subtitle = "Check to see if there are any applications to review";
      buttonText = "Review Applications";
      buttonUrl = "/b/bookings/new";
      break;
    case BookingTabs.Completed:
      header = "No Completed Bookings";
      subtitle =
        "You can review and complete any in progress via the 'In Progress' tab";
      buttonText = "";
      buttonUrl = "/b/bookings/in_progress";
      break;
    case BookingTabs.Archived:
      header = "No Archived Bookings";
      subtitle =
        "You'll find all your archived bookings here as soon as that changes";
      buttonText = "";
      buttonUrl = "";
      break;
  }

  return (
    <EmptyStateWrap>
      <Flex
        margin="xxxl 0 0"
        justify="center"
        direction="column"
        align="center"
        style={{ textAlign: "center" }}
      >
        <ListingEmptyStateIcon />
        <H2 margin="xxl 0 m">{header}</H2>
        <Subtitle margin="0">{subtitle}</Subtitle>
        {buttonUrl && buttonText ? (
          <div>
            <CTA margin="l 0 0" to={buttonUrl}>
              {buttonText}
            </CTA>
          </div>
        ) : null}
      </Flex>
    </EmptyStateWrap>
  );
};

interface RenderBookingProps {
  bookings: BrandBookingsQuery["bookings"]["bookings"];
  showDate?: boolean;
  bookingType: BookingTabs;
  lastElementRef: (node: HTMLDivElement | null) => void;
  activeBookingId?: string | null;
  isDemo: boolean;
}

export const RenderBookingList = ({
  bookings,
  bookingType,
  lastElementRef,
  activeBookingId,
  isDemo,
}: RenderBookingProps) => {
  if (bookings.length === 0) {
    return <Text align="center">No {bookingType.toLowerCase()} bookings</Text>;
  }

  // If in demo mode, reverse the order of bookings
  const displayBookings =
    isDemo && bookingType === BookingTabs.New
      ? [...bookings].reverse()
      : bookings;

  return (
    <div>
      {displayBookings.map((b, index) => (
        <div
          ref={index === bookings.length - 3 ? lastElementRef : null}
          key={b.id}
        >
          <BookingCardLink
            isActive={
              activeBookingId && activeBookingId === b.id ? true : false
            }
            bookingType={bookingType}
            booking={b}
          />
        </div>
      ))}
    </div>
  );
};

const BookingCardLink = ({
  booking,
  bookingType,
  isActive,
}: {
  booking: BrandBookingsQuery["bookings"]["bookings"][0];
  bookingType: BookingTabs;
  isActive: boolean;
}) => {
  const { booking: enhancedBooking } = useDemoBookingData(booking);
  const history = useHistory();
  const location = history.location;

  // Get current query parameters to preserve them when changing bookings
  const currentQueryParams = new URLSearchParams(location.search).toString();
  const linkTo = currentQueryParams
    ? `/b/bookings/${bookingType}/${booking.id}?${currentQueryParams}`
    : `/b/bookings/${bookingType}/${booking.id}`;

  if (!booking.creator.preferredProfile) {
    return null;
  }

  const hasNotifications =
    booking.actionRequired ||
    booking.unreadMessageCount > 0 ||
    booking.numSubmissionsToReview > 0;

  return (
    <StyledCardLink isActive={isActive} to={linkTo} key={booking.id}>
      {booking.creator.tier === CreatorTier.CreatorTierPremium ? (
        <PremiumBadge>
          <StarIcon colorPreset="reward" height={12} width={12} />
          <span>Top Rated</span>
        </PremiumBadge>
      ) : null}
      <Flex direction="row" justify="space-between" align="center" margin="m">
        <Flex align="center">
          <Avatar
            withShadow={false}
            borderSize={0}
            size={42}
            url={booking.creator.preferredProfile.avatar}
          />
          <Flex direction="column" margin="0 0 0 s">
            <CardName
              colorPreset="heading"
              weight="bold"
              isCompact
              truncate
              margin="0"
            >
              {booking.creator.preferredProfile.name.length > 18
                ? booking.creator.preferredProfile.name.slice(0, 18) + "..."
                : booking.creator.preferredProfile.name}
            </CardName>
            <CardUsername colorPreset="secondary" margin="0" truncate>
              @
              {booking.creator.preferredProfile.username.length > 18
                ? booking.creator.preferredProfile.username.slice(0, 18) + "..."
                : booking.creator.preferredProfile.username}
            </CardUsername>
          </Flex>
        </Flex>
        <Flex
          align="center"
          justify="center"
          margin="0 0 0 0"
          style={{ flexShrink: 0 }}
        >
          <Flex align="center" justify="center">
            {hasNotifications ? (
              <NotificationIndicator isActive={true} />
            ) : null}
            {generateStatusText(enhancedBooking, bookingType)}
          </Flex>
        </Flex>
      </Flex>
    </StyledCardLink>
  );
};

function generateStatusText(
  enhancedBooking: BrandBookingsQuery["bookings"]["bookings"][0],
  bookingType: BookingTabs
) {
  if (bookingType === BookingTabs.Archived) {
    return (
      <Text margin="xxs 0 0" size="xxxs" colorPreset="secondary">
        {enhancedBooking.status === BookingStatus.BookingStatusCancelled
          ? "Cancelled "
          : enhancedBooking.status === BookingStatus.BookingStatusCompleted
          ? "Completed "
          : enhancedBooking.status === BookingStatus.BookingStatusExpired
          ? "Expired "
          : "Declined "}
        {formatDistanceToNowStrict(fromUnixTime(enhancedBooking.updatedAt), {
          addSuffix: true,
        })}
      </Text>
    );
  }

  if (bookingType === BookingTabs.New) {
    let expiresAt = fromUnixTime(enhancedBooking.expiresAt);
    const isExpiringInThreeDays = isBefore(subDays(expiresAt, 3), new Date());

    if (
      enhancedBooking.status ===
        BookingStatus.BookingStatusCreatorRescheduled &&
      enhancedBooking.approvedAt
    ) {
      return (
        <Flex direction="column" align="flex-end">
          <Text margin="xxs 0 0" size="xxxs" colorPreset="link">
            Reschedule request
          </Text>
          <Text
            margin="xxs 0 0"
            size="xxxs"
            colorPreset={isExpiringInThreeDays ? "warning" : "secondary"}
          >
            Expires{" "}
            {formatDistanceToNowStrict(expiresAt, {
              addSuffix: true,
            })}
          </Text>
        </Flex>
      );
    }

    return (
      <Text
        margin="xxs 0 0"
        size="xxxs"
        colorPreset={isExpiringInThreeDays ? "warning" : "secondary"}
      >
        Expires{" "}
        {formatDistanceToNowStrict(expiresAt, {
          addSuffix: true,
        })}
      </Text>
    );
  }

  const updatedAt =
    enhancedBooking.lastActivityDate || enhancedBooking.updatedAt;

  return (
    <Text margin="xxs 0 0" size="xxxs" colorPreset="secondary">
      Updated{" "}
      {formatDistanceToNowStrict(fromUnixTime(updatedAt), {
        addSuffix: true,
      })}
    </Text>
  );
}

const CardName = styled(Text)`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  overflow: hidden;
  text-overflow: ellipsis;

  font-size: ${(p) => p.theme.typography.size.s};
  @media (min-width: ${XL_DESKTOP_BREAKPOINT}px) {
    font-size: ${(p) => p.theme.typography.size.m};
  }
`;

const CardUsername = styled(Text)`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  overflow: hidden;
  text-overflow: ellipsis;

  font-size: ${(p) => p.theme.typography.size.s};
  @media (min-width: ${XL_DESKTOP_BREAKPOINT}px) {
    font-size: ${(p) => p.theme.typography.size.m};
  }
`;

const PremiumBadge = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  padding: 0 ${(p) => p.theme.spacing.s};
  border-bottom-left-radius: ${(p) => p.theme.misc.borderRadiusXSmall};
  background-color: ${(p) => p.theme.color.reward};
  color: ${(p) => p.theme.color.typography.heading};
  font-weight: ${(p) => p.theme.typography.weight.bold};
  display: flex;
  align-items: center;
  gap: ${(p) => p.theme.spacing.xxs};

  svg {
    margin-top: -1px;
  }

  span {
    margin-top: 1px;
    font-size: ${(p) => p.theme.typography.size.xxxs};
    font-weight: ${(p) => p.theme.typography.weight.bold};
    font-family: ${(p) => p.theme.typography.bodyFamily};
    margin-left: ${(p) => p.theme.spacing.xxs};
    color: ${(p) => p.theme.color.typography.reward};
  }
`;

const NotificationIndicator = styled.span<{ isActive: boolean }>`
  height: 5px;
  width: 5px;
  border-radius: 100%;
  background-color: ${(p) => p.theme.color.primary};
  opacity: ${(p) => (p.isActive ? 1 : 0)};
  transition: opacity 150ms ease-out;
  margin-right: ${(p) => p.theme.spacing.s};
`;
